/**
 * 작성자 : 박승호
 * 날짜 : 2025.06.12
 * 경로 : 설정관리-방문자 설정관리 - 재교육 기간 설정 탭
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSetAuth } from '../../../utils/useSetAuth';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { BtnBlue } from '../../../components/Button';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { apiGet, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import RadioContainer from '../../../components/button/RadioContainer';
import IssueGuide from '../../../components/IssueGuide';

const Root = styled.div`
  align-items: center;
  overflow: auto !important;
  .inputForm {
    /* position: absolute; */
    width: 100%;
    padding: 1rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    overflow: auto;
    @media (min-width: 1025px) {
      padding: 3rem;
      width: 40rem;
    }
    .common {
      background-color: rgba(255, 0, 0, 0.05);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      height: 3rem;
      margin: 1rem 0;
      padding: 0 1rem;
      color: red;
      span.material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48;
      }
    }
    .title {
      border-bottom: 1px solid var(--outline);
      color: var(--text-primary);
      height: 3rem;
      display: flex;
      align-items: center;
      font-weight: 600;
    }
    .setting-flex {
      display: flex;
      flex-direction: column;
      padding: 1rem 0rem;
      gap: 1rem;
    }
    .setting-row {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      @media (min-width: 768px) {
        flex-direction: row;
        justify-content: space-between;
      }
      .textGroup {
        label {
          font-weight: 500;
          color: var(--text-primary);
        }
        p {
          font-size: 0.875rem;
          color: var(--text-tertiary);
        }
      }
      .textInInput {
        background-color: var(--tonal);
        border-radius: 0.25rem;
        display: flex;
        align-items: center;
        width: 6rem;
        input {
          outline: none;
          text-align: center;
          background-color: transparent;
          font-weight: 500;
          padding: 0;
        }
        span {
          border-left: 1px solid var(--outline);
          padding: 0 0.75rem;
          user-select: none;
          color: var(--text-tertiary);
        }
      }
      .radioContainer {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    }
  }
  .buttonsWrapper {
    display: flex;
    justify-content: flex-start;
    @media (min-width: 768px) {
      justify-content: flex-end;
    }
  }

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

const PersonalInfoType = {
  NONE: 0,
  BDATE: 1,
  HNUM: 2,
  ALL: 3,
};

const EdutermSetting = () => {
  const { auth } = useSetAuth();
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const [visitEduTerm, setVisitEduTerm] = useState<number>(0);
  const visitEduTermRef = useRef<HTMLInputElement>(null);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [personalInfoType, setPersonalInfoType] = useState<number>(PersonalInfoType.NONE); //  0: 모두 미사용, 1: 생년월일, 2: 연락처, 3: 모두사용
  const { data, isError } = useQuery(['edutermSetting'], () => getEdutermSettingAPI(), { enabled: !!userInfo.hCd && !!userInfo.sCd });
  const [settingYn, setSettingYn] = useState<{
    [key: string]: boolean;
  }>({
    carnumYn: false,
    photoYn: false,
    videoSkipYn: false,
    gubunYn: true,
    revisitYn: true,
    visitUseYn: false,
  });

  const getEdutermSettingAPI = async () => {
    const { hCd, sCd } = userInfo;
    try {
      const res = await apiGet({ path: '/visit/setting', contentType: 'application/json', req: { hCd, sCd } });
      return res.data.data;
    } catch (err) {
      console.error('err : ', err);
      throw new Error('재교육 기간 조회 실패');
    }
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setVisitEduTerm(Number(value));
    }
  };

  const onClickSave = async () => {
    setIsSaveClicked(true);

    if (visitEduTermRef.current?.value === '') {
      toast.warning(t('빈칸을 입력하세요'));
      return visitEduTermRef.current?.focus();
    }

    if (visitEduTerm > 1095) {
      toast.warning(t('재교육기간은 최대 1095일까지 입력 가능합니다.'));
      return visitEduTermRef.current?.focus();
    }

    const { hCd, sCd } = userInfo;
    try {
      const res = await apiPost({
        path: '/visit/setting',
        contentType: 'application/json',
        req: {
          hCd,
          sCd,
          visitEduTerm,
          visitBdateYn: personalInfoType === PersonalInfoType.ALL || personalInfoType === PersonalInfoType.BDATE ? 'Y' : 'N',
          visitHnumYn: personalInfoType === PersonalInfoType.ALL || personalInfoType === PersonalInfoType.HNUM ? 'Y' : 'N',
          carnumYn: settingYn.carnumYn ? 'Y' : 'N',
          photoYn: settingYn.photoYn ? 'Y' : 'N',
          videoSkipYn: settingYn.videoSkipYn ? 'Y' : 'N',
          gubunYn: settingYn.gubunYn ? 'Y' : 'N',
          revisitYn: settingYn.revisitYn ? 'Y' : 'N',
          visitUseYn: settingYn.visitUseYn ? 'Y' : 'N',
        },
      });
      const { statusCode } = res.data;

      if (statusCode === 200) {
        toast.success(t('설정 정보 저장 성공'));
      }

      return false;
    } catch (err) {
      console.error('err : ', err);
      toast.error(t('저장중 오류가 발생했습니다.'));
      throw new Error('재교육 기간 저장 실패');
    }
  };

  useEffect(() => {
    if (data) {
      setVisitEduTerm(data.visitEduTerm);
      const infotype =
        data.visitBdateYn === 'Y' && data.visitHnumYn === 'Y'
          ? PersonalInfoType.ALL
          : data.visitBdateYn === 'Y'
          ? PersonalInfoType.BDATE
          : data.visitHnumYn === 'Y'
          ? PersonalInfoType.HNUM
          : PersonalInfoType.NONE;
      setPersonalInfoType(infotype);
      setSettingYn({
        carnumYn: data.carnumYn === 'Y',
        photoYn: data.photoYn === 'Y',
        videoSkipYn: data.videoSkipYn === 'Y',
        gubunYn: data.gubunYn === 'Y',
        revisitYn: data.revisitYn === 'Y',
        visitUseYn: data.visitUseYn === 'Y',
      });
    }
  }, [data]);

  if (isError) return <IssueGuide type='error' text={t('조회중 오류가 발생했습니다.')} />;

  return (
    <Root>
      <div className='inputForm'>
        <div className='setting-container'>
          <div className='setting-row'>
            <div className='textGroup'>
              <label htmlFor='eduTerm'>{t('방문자 사용 여부')}</label>
              {/* <p>
                {t('일수. 예: 1년')}&rarr; 365. {t('최대 3년(1095일)')}
              </p> */}
            </div>
            <div className='radioContainer'>
              <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, visitUseYn: true })} isSelected={settingYn.visitUseYn} />
              <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, visitUseYn: false })} isSelected={!settingYn.visitUseYn} />
            </div>
          </div>
        </div>

        <div className='setting-container'>
          <div className='title'>{t('재교육 기간 설정')}</div>
          <div className='common'>
            <span className='material-symbols-rounded'>warning</span>
            {t('각 항목에 0 입력 시 미적용')}
          </div>
          <div className='setting-row'>
            <div className='textGroup'>
              <label htmlFor='eduTerm'>{t('재교육 기간')}</label>
              <p>
                {t('일수. 예: 1년')}&rarr; 365. {t('최대 3년(1095일)')}
              </p>
            </div>
            <div className='textInInput'>
              <input
                type='text'
                id='visitEduTerm'
                name='visitEduTerm'
                value={visitEduTerm}
                ref={visitEduTermRef}
                onChange={onChangeInput}
                disabled={!auth.createAuth && !auth.updateAuth}
                style={isSaveClicked ? applyBorderStyle(visitEduTerm, 'red', 'eduTerm') : undefined}
              />
              <span>{t('일')}</span>
            </div>
          </div>
        </div>

        <div className='setting-container'>
          <div className='title'>{t('방문자 등록 설정')}</div>
          <div className='setting-flex'>
            <div className='setting-row' style={{ flexDirection: 'column' }}>
              <div className='textGroup'>
                <label htmlFor='eduTerm'>{t('개인정보 식별 방법')}</label>
                <p>{t('생년월일/연락처 중 개인정보 식별 방법 선택')}</p>
              </div>
              <div className='radioContainer' style={{ justifyContent: 'flex-end' }}>
                <RadioContainer name={t('모두 미사용')} onClick={() => setPersonalInfoType(PersonalInfoType.NONE)} isSelected={personalInfoType === PersonalInfoType.NONE} />
                <RadioContainer name={t('생년월일')} onClick={() => setPersonalInfoType(PersonalInfoType.BDATE)} isSelected={personalInfoType === PersonalInfoType.BDATE} />
                <RadioContainer name={t('연락처')} onClick={() => setPersonalInfoType(PersonalInfoType.HNUM)} isSelected={personalInfoType === PersonalInfoType.HNUM} />
                <RadioContainer name={t('모두 사용')} onClick={() => setPersonalInfoType(PersonalInfoType.ALL)} isSelected={personalInfoType === PersonalInfoType.ALL} />
              </div>
            </div>
            <div className='setting-row'>
              <div className='textGroup'>
                <label htmlFor='eduTerm'>{t('재방문 사용')}</label>
                <p>{t('재방문 사용 여부')}</p>
              </div>
              <div className='radioContainer'>
                <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, revisitYn: true })} isSelected={settingYn.revisitYn} />
                <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, revisitYn: false })} isSelected={!settingYn.revisitYn} />
              </div>
            </div>
            <div className='setting-row'>
              <div className='textGroup'>
                <label htmlFor='eduTerm'>{t('차량 번호 입력')}</label>
                <p>{t('차량 번호 입력 사용 여부')}</p>
              </div>
              <div className='radioContainer'>
                <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, carnumYn: true })} isSelected={settingYn.carnumYn} />
                <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, carnumYn: false })} isSelected={!settingYn.carnumYn} />
              </div>
            </div>
            <div className='setting-row'>
              <div className='textGroup'>
                <label htmlFor='eduTerm'>{t('사진 촬영')}</label>
                <p>{t('사진 촬영 사용 여부')}</p>
              </div>
              <div className='radioContainer'>
                <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, photoYn: true })} isSelected={settingYn.photoYn} />
                <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, photoYn: false })} isSelected={!settingYn.photoYn} />
              </div>
            </div>
            <div className='setting-row'>
              <div className='textGroup'>
                <label htmlFor='gubunYn'>{t('방문자 구분 사용 여부')}</label>
                <p>{t('방문자 유형 구분(차량 운전원 / 일반 방문자) 사용 여부')}</p>
              </div>
              <div className='radioContainer'>
                <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, gubunYn: true })} isSelected={settingYn.gubunYn} />
                <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, gubunYn: false })} isSelected={!settingYn.gubunYn} />
              </div>
            </div>
          </div>
        </div>

        <div className='setting-container'>
          <div className='title'>{t('교육 영상 설정')}</div>
          <div className='setting-flex'>
            <div className='setting-row'>
              <div className='textGroup'>
                <label htmlFor='eduTerm'>{t('영상 필수 시청 여부')}</label>
                <p>{t('영상을 필수로 시청 후 다음 진행에 대한 사용 여부')}</p>
              </div>
              <div className='radioContainer'>
                <RadioContainer name={t('사용')} onClick={() => setSettingYn({ ...settingYn, videoSkipYn: true })} isSelected={settingYn.videoSkipYn} />
                <RadioContainer name={t('미사용')} onClick={() => setSettingYn({ ...settingYn, videoSkipYn: false })} isSelected={!settingYn.videoSkipYn} />
              </div>
            </div>
          </div>
        </div>

        {(auth.createAuth || auth.updateAuth) && (
          <div className='buttonsWrapper'>
            <div className='flexRow'>
              <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>
            </div>
          </div>
        )}
      </div>
    </Root>
  );
};

export default EdutermSetting;
